import { LOCATION_CHANGE } from 'react-router-redux';
import { sendLog, LoggerStatusEnum } from 'ui-model';
import { fromJS } from 'immutable';
import { get, has, isString, filter, find } from 'lodash';
import { getType } from 'typesafe-actions';

import { updateLoading, signOut } from '@lib/actions';

import {
  CLEAR_NOTIFICATION,
  CLOSE_WEB_NOTIFICATION,
  GET_BROADCASTS_COUNT_SUCCESS,
  GET_DOWNTIME_MESSAGE_SUCCESS,
  GET_LANGUAGES,
  GET_LANGUAGES_ERROR,
  GET_LANGUAGES_SUCCESS,
  HIDE_EXPIRING_PASSWORD_ALERT,
  IGNORE_WEB_NOTIFICATION,
  LOAD_INTAKE_FORMS_SQUIGGLIES,
  LOAD_INTAKE_FORMS_SQUIGGLIES_ERROR,
  LOAD_INTAKE_FORMS_SQUIGGLIES_SUCCESS,
  LOAD_WIDGET_USER,
  LOAD_WIDGET_USER_ERROR,
  LOAD_WIDGET_USER_SUCCESS,
  ON_SOCKET_MESSAGE,
  SET_ACHIEVEMENT_TYPE,
  SHOW_EXPIRING_PASSWORD_ALERT,
  WEB_NOTIFICATION_SHOWED,
  ON_SOCKET_PRESENCE_USER_ONLINE,
  ON_SOCKET_PRESENCE_USER_OFFLINE,
  LOAD_PATIENT_JOURNEY_SUCCESS,
} from './constants';

const initialState = fromJS({
  achievementType: '',
  broadcastsCount: 0,
  currentUser: false,
  downtimeMessage: '',
  error: false,
  intakeFormsSquigglies: false,
  languages: false,
  loading: false,
  masterUser: false,
  newMessage: [],
  notification: false,
  referrals: false,
  showExpiringPasswordAlert: false,
  webNotificationData: false,
  webNotificationIgnore: false,
  webNotificationTitle: '',
  webNotificationType: '',
  onlineUsers: [],
  offlineUsers: [],
  patientJourney: false,
});

const reducer = (s = initialState, action) => {
  /**
   * Action group rules:

   * Simple actions: For example, non promise actions
   * Request actions: Promise or not, this actions will fire a response action
   * Success response actions: Success action, fired by the request action can
   *                           contains database information for example.
   * Error response actions: Error action fired when something goes wrong with
   *                         the request action
  */
  let state = s;

  // WORKAROUND skip messages for 404 errors on `GET /patients/:id`
  if (
    action.error &&
    !action.dismissSnackbar &&
    !get(action, 'error.dismissSnackbar') &&
    !(action.error.status === 404 && /patients\//i.test(action.error.url)) &&
    action.type !== '@@redux-form/SET_SUBMIT_FAILED' &&
    !get(action, 'error.config.ignoreError')
  ) {
    const message = isString(action.error) ? action.error : action.error.statusText || action.error.message;
    if (!message) {
      sendLog("No error message for reducer's action", action.error, LoggerStatusEnum.info);
    }
    state = state.set('error', {
      message: message || 'Something went wrong',
      // add timestamp in order to refresh error in case of being the same message
      timestamp: new Date().getTime(),
    });
  }

  if (action.notification) state = state.set('notification', action.notification);

  if (has(action, 'loading')) {
    state = state.set('loading', action.loading);
  } else if (/_SUCCESS|_ERROR/.test(action.type)) {
    state = state.set('loading', false);
  }
  if (action.type === getType(updateLoading)) {
    state = state.set('loading', action.payload);
  }

  // clear global error reducer state when location changes
  if (action.type === LOCATION_CHANGE) state = state.set('error', false);

  switch (action.type) {
    // Simple actions
    case CLEAR_NOTIFICATION:
      return state.set('notification', false);
    case CLOSE_WEB_NOTIFICATION:
      return state
        .set('webNotificationType', '')
        .set('webNotificationTitle', '')
        .set('webNotificationData', false)
        .set('webNotificationIgnore', false);
    case HIDE_EXPIRING_PASSWORD_ALERT:
      return state.set('showExpiringPasswordAlert', false);
    case IGNORE_WEB_NOTIFICATION:
      return state.set('webNotificationIgnore', true);
    case SET_ACHIEVEMENT_TYPE:
      return state.set('achievementType', action.achievementType);
    case SHOW_EXPIRING_PASSWORD_ALERT:
      return state.set('showExpiringPasswordAlert', true);
    case getType(signOut):
      return state.set('currentUser', false);
    // Request actions
    case GET_LANGUAGES:
    case LOAD_WIDGET_USER:
    case LOAD_INTAKE_FORMS_SQUIGGLIES:
      return state.set('error', false);
    // Error response actions
    case GET_LANGUAGES_ERROR:
    case LOAD_WIDGET_USER_ERROR:
    case LOAD_INTAKE_FORMS_SQUIGGLIES_ERROR:
      return state.set('error', action.error);
    // Success response actions
    case GET_LANGUAGES_SUCCESS:
      return state.set('languages', action.response);
    case LOAD_WIDGET_USER_SUCCESS:
      return state.set('widgetUser', action.response);
    case GET_BROADCASTS_COUNT_SUCCESS:
      return state.set('broadcastsCount', action.response);
    case GET_DOWNTIME_MESSAGE_SUCCESS:
      return state.set('downtimeMessage', action.downtimeMessage);
    case LOAD_INTAKE_FORMS_SQUIGGLIES_SUCCESS:
      return state.set('intakeFormsSquigglies', action.intakeFormsSquigglies).set('error', false);
    case ON_SOCKET_MESSAGE: {
      return state.set('newMessage', [...state.get('newMessage'), action.message]);
    }
    case WEB_NOTIFICATION_SHOWED:
      return state
        .set('webNotificationType', action.data.type)
        .set('webNotificationTitle', action.data.title)
        .set('webNotificationData', action.data.data)
        .set('webNotificationIgnore', false);
    case ON_SOCKET_PRESENCE_USER_ONLINE: {
      const onlineUsers = state.get('onlineUsers');
      const offlineUsers = state.get('offlineUsers');

      const userInOnlineList = !!find(onlineUsers, (item) => item === action.data.user);
      const userInOfflineList = !!find(offlineUsers, (item) => item === action.data.user);

      return state
        .set('onlineUsers', userInOnlineList ? onlineUsers : [...onlineUsers, action.data.user])
        .set(
          'offlineUsers',
          userInOfflineList ? filter(offlineUsers, (offlineUser) => offlineUser !== action.data.user) : offlineUsers,
        );
    }
    case ON_SOCKET_PRESENCE_USER_OFFLINE: {
      // we check offline users to prevent display wrong online data
      const onlineUsers = state.get('onlineUsers');
      const offlineUsers = state.get('offlineUsers');

      const userInOnlineList = !!find(onlineUsers, (item) => item === action.data.user);
      const userInOfflineList = !!find(offlineUsers, (item) => item === action.data.user);

      return state
        .set(
          'onlineUsers',
          userInOnlineList ? filter(onlineUsers, (onlineUser) => onlineUser !== action.data.user) : onlineUsers,
        )
        .set('offlineUsers', userInOfflineList ? offlineUsers : [...offlineUsers, action.data.user]);
    }
    case LOAD_PATIENT_JOURNEY_SUCCESS:
      return state.set('patientJourney', action.response);
    default:
      return state;
  }
};

export default reducer;
